// @ts-check

const OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION = 3;

// Montagewerte der aktuell bereitgestellten Sanitaerobjekte. Der
// modelRotationOffsetDeg dreht ausschliesslich das 3D-Modell relativ zur
// korrekten 2D-Draufsicht; die vom Benutzer gewaehlte Objektdrehung bleibt
// davon getrennt.
//
// urinal/lavabo hatten hier bis 2026-08-28 modelRotationOffsetDeg: 180 --
// seither steht das 3D-Modell dieser beiden Artikel gegenueber der 2D-
// Draufsicht (und dem davon abgeleiteten DXF/STEP-Export) um 180 Grad
// verdreht, waehrend wc (modelRotationOffsetDeg 0) korrekt blieb
// (Nutzer-Meldung 2026-08-28). Die VERSION wurde auf 3 angehoben, damit
// bereits platzierte urinal/lavabo-Objekte in bestehenden Projekten beim
// naechsten Laden automatisch auf den neuen Wert migrieren.
const OBJECT_ASSET_PLACEMENT_DEFAULTS = new Map([
  ['wc', Object.freeze({ floorOffsetMm: 160, modelRotationOffsetDeg: 0 })],
  ['urinal', Object.freeze({ floorOffsetMm: 500, modelRotationOffsetDeg: 0 })],
  ['lavabo', Object.freeze({ floorOffsetMm: 750, modelRotationOffsetDeg: 0 })]
]);

function normalizedAssetKey(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  let decoded = text;
  try {
    decoded = decodeURIComponent(text);
  } catch (_error) {
    // Ein nicht URL-codierter Anzeigename ist bereits direkt auswertbar.
  }
  const basename = decoded.split(/[\\/]/).at(-1)?.split(/[?#]/)[0] || '';
  return basename.replace(/\.(?:step|stp|svg|dxf|glb|json)$/i, '').trim().toLowerCase();
}

function objectAssetPlacementDefaults(value = {}) {
  const candidates = [
    value.id,
    value.fileStem,
    value.articleNumber,
    value.assetFile,
    value.modelPath,
    value.svgPath,
    value.stepPath,
    value.name,
    value.label
  ];
  for (const candidate of candidates) {
    const key = normalizedAssetKey(candidate);
    const defaults = OBJECT_ASSET_PLACEMENT_DEFAULTS.get(key);
    if (defaults) return { key, ...defaults };
  }
  return null;
}

function defaultObjectAssetFloorOffsetMm(value = {}, suppliedValue = undefined) {
  const numeric = Number(suppliedValue);
  const defaults = objectAssetPlacementDefaults(value);
  if (defaults && (!Number.isFinite(numeric) || numeric === 0)) return defaults.floorOffsetMm;
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

function normalizedObjectAssetFloorOffsetMm(value = {}, storedValue = undefined, storedVersion = undefined) {
  const numeric = Number(storedValue);
  const version = Number(storedVersion);
  const defaults = objectAssetPlacementDefaults(value);
  const needsMigration = Boolean(defaults)
    && (!Number.isFinite(version) || version < OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION)
    && (!Number.isFinite(numeric) || numeric === 0);
  if (needsMigration) return defaults.floorOffsetMm;
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

function normalizedObjectAssetModelRotationOffsetDeg(value = {}, storedValue = undefined, storedVersion = undefined) {
  const numeric = Number(storedValue);
  const version = Number(storedVersion);
  const defaults = objectAssetPlacementDefaults(value);
  const needsMigration = Boolean(defaults)
    && (!Number.isFinite(version) || version < OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION);
  if (needsMigration) return defaults.modelRotationOffsetDeg || 0;
  return Number.isFinite(numeric) ? numeric : (defaults?.modelRotationOffsetDeg || 0);
}

function objectAssetModelRotationOffsetRad(value = {}) {
  const explicit = Number(value.modelRotationOffsetDeg);
  const degrees = Number.isFinite(explicit)
    ? explicit
    : (objectAssetPlacementDefaults(value)?.modelRotationOffsetDeg || 0);
  return degrees * Math.PI / 180;
}

export {
  OBJECT_ASSET_PLACEMENT_DEFAULTS_VERSION,
  defaultObjectAssetFloorOffsetMm,
  normalizedObjectAssetFloorOffsetMm,
  normalizedObjectAssetModelRotationOffsetDeg,
  objectAssetModelRotationOffsetRad,
  objectAssetPlacementDefaults
};
